import {
  Checkbox,
  Conditional,
  DateInput,
  Field,
  Textarea,
} from '@payaca/ambrosia';
import { FC, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

/**
 * The subset of purchase order form fields shared by the project and stock
 * location create flows. Delivery is intentionally NOT included — it differs
 * between flows (an address picker vs a fixed location address) and is supplied
 * via the `deliverTo` slot. Direct purchase order fields are never referenced
 * here: those live on their own panels in the project flow.
 */
export type PurchaseOrderDetailFormValues = {
  dueAt?: Date;
  additionalNotes?: string;
  showMaterialPrices: boolean;
};

type Props = {
  /** The "Deliver to" control for this flow. */
  deliverTo: ReactNode;
  /**
   * Whether to render the Due by input. The project flow omits it while the
   * panel is animating off-screen; defaults to shown.
   */
  renderDueAtInput?: boolean;
  /** Marks the Due by field invalid (the project flow requires it on submit). */
  dueAtInvalid?: boolean;
};

const PurchaseOrderDetailFields: FC<Props> = ({
  deliverTo,
  renderDueAtInput = true,
  dueAtInvalid = false,
}) => {
  const { control, register } = useFormContext<PurchaseOrderDetailFormValues>();

  return (
    <>
      <Field.Legacy
        validationState={
          dueAtInvalid
            ? { isValid: false, validationMessages: ['Due by required'] }
            : undefined
        }
      >
        <Field.Label>Due by</Field.Label>
        <Conditional condition={renderDueAtInput}>
          <Controller
            control={control}
            name="dueAt"
            render={({ field: { value, onChange } }) => (
              <DateInput value={value} onChange={onChange} />
            )}
          />
        </Conditional>
      </Field.Legacy>

      <Field.Legacy>
        <Field.Label>Deliver to</Field.Label>
        {deliverTo}
      </Field.Legacy>

      <Field.Legacy name="additionalNotes">
        <Field.Label>Additional notes</Field.Label>
        <Controller
          control={control}
          name="additionalNotes"
          render={({ field: { value, onChange, onBlur } }) => (
            <Textarea
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              autoHeight
            />
          )}
        />
      </Field.Legacy>

      <Field.Legacy name="showMaterialPrices">
        <Checkbox
          label="Show material prices on purchase order"
          {...register('showMaterialPrices')}
        />
      </Field.Legacy>
    </>
  );
};

export default PurchaseOrderDetailFields;
